import { create } from 'zustand'
import {
  collection,
  updateDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  doc,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage'
import { auth, db, functions, storage } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'
import { isTournamentArchived } from '@/utils/tournamentRegistration'

const LOGO_MAX_DIMENSION = 800
const LOGO_MAX_BYTES = 5 * 1024 * 1024
const LOGO_MIME = 'image/webp'

async function resizeToWebp (file: any, quality = 0.85): Promise<any> {
  if (!file || !file.type?.startsWith('image/')) return file
  try {
    const bitmap = await createImageBitmap(file)
    const { width: w, height: h } = bitmap
    const scale = Math.min(1, LOGO_MAX_DIMENSION / Math.max(w, h))
    const targetW = Math.max(1, Math.round(w * scale))
    const targetH = Math.max(1, Math.round(h * scale))
    const canvas = document.createElement('canvas')
    canvas.width = targetW
    canvas.height = targetH
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
    ctx.drawImage(bitmap, 0, 0, targetW, targetH)
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, LOGO_MIME, quality)
    )
    return blob || file
  } catch (e) {
    console.warn('Tournament logo resize failed:', e)
    return file
  }
}

function logoStoragePath (tournamentId, uid, ext = 'webp') {
  if (!tournamentId || !uid) throw new Error('tournament and uid required')
  return `tournament-logos/${tournamentId}/${uid}/logo-${Date.now()}.${ext}`
}

function randomGameId () {
  return `game_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

function normalizeGameDate (value) {
  if (value == null || value === '') return null
  if (typeof value?.toDate === 'function') return value
  if (value instanceof Timestamp) return value
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : Timestamp.fromDate(value)
  }
  if (typeof value === 'string') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d)
  }
  return null
}

export interface TournamentsSubscribeOptions {
  includeHidden?: boolean
  adminTournamentIds?: string[]
}

export interface NotifyTournamentPublishOptions {
  tournamentId?: string
  includeDraft?: boolean
  includeSchedule?: boolean
  force?: boolean
}

export interface TournamentsState {
  /** Live tournaments; archived ones are never in here (see `archivedList`). */
  list: any[]
  /** Archived tournaments the listener can see (super admins: hub "Archived" section). */
  archivedList: any[]
  loading: boolean
  // derived
  listSorted: any[]
  // actions
  subscribeAll: (opts?: TournamentsSubscribeOptions) => void
  stopList: () => void
  createTournament: (payload?: { program?: string, [key: string]: any }) => Promise<{ success: boolean, id?: string, error?: string }>
  updateTournament: (tournamentId: string, data: any) => Promise<{ success: boolean }>
  setTournamentArchived: (tournamentId: string, archived: boolean) => Promise<{ success: boolean, error?: string }>
  saveGame: (tournamentId: string, gameData: any) => Promise<{ success: boolean, gameId?: string, error?: string }>
  deleteGame: (tournamentId: string, gameId: string) => Promise<{ success: boolean, error?: string }>
  notifyTournamentPublish: (opts?: NotifyTournamentPublishOptions) => Promise<{ success: boolean, error?: string, [key: string]: any }>
  uploadLogo: (file: File | Blob, tournamentId: string) => Promise<{ url: string, path: string }>
  deleteLogoByPath: (path: string | null | undefined) => Promise<void>
}

// Module-level listener bookkeeping (was closure state in the Pinia setup store).
let listUnsubs: Array<() => void> = []
let activeSubscribeKey: string | null = null
let sourceMaps: Array<Map<string, any>> = []

export const useTournamentsStore = create<TournamentsState>()(
  derived<TournamentsState>((s) => ({
    listSorted: [...s.list].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }))((set, get) => {
    function emitMergedList () {
      const merged = new Map()
      for (const sourceMap of sourceMaps) {
        for (const [id, data] of sourceMap.entries()) {
          merged.set(id, data)
        }
      }
      const list: any[] = []
      const archivedList: any[] = []
      for (const t of merged.values()) (isTournamentArchived(t) ? archivedList : list).push(t)
      set({ list, archivedList })
    }

    function attachQuerySource (q, label) {
      const sourceMap = new Map()
      sourceMaps.push(sourceMap)
      const unsub = onSnapshot(
        q,
        (snap: any) => {
          sourceMap.clear()
          for (const d of snap.docs) {
            sourceMap.set(d.id, { id: d.id, ...d.data() })
          }
          emitMergedList()
        },
        (err) => console.error(`Tournaments list (${label}):`, err)
      )
      listUnsubs.push(() => {
        unsub()
        sourceMaps = sourceMaps.filter((m) => m !== sourceMap)
        emitMergedList()
      })
    }

    function attachDocSource (tournamentId) {
      if (!tournamentId) return
      const sourceMap = new Map()
      sourceMaps.push(sourceMap)
      const dref = doc(db, 'tournaments', tournamentId)
      const unsub = onSnapshot(
        dref,
        (snap) => {
          sourceMap.clear()
          if (snap.exists()) {
            sourceMap.set(snap.id, { id: snap.id, ...snap.data() })
          }
          emitMergedList()
        },
        (err) => console.error(`Tournament doc (${tournamentId}):`, err)
      )
      listUnsubs.push(() => {
        unsub()
        sourceMaps = sourceMaps.filter((m) => m !== sourceMap)
        emitMergedList()
      })
    }

    /**
     * Public/normal users query visible tournaments only.
     * Super admins can query all tournaments, including hidden drafts.
     * Tournament admins additionally subscribe to each assigned id so hidden drafts
     * they manage still appear without requiring broad hidden-query access.
     */
    const subscribeAll = (opts: TournamentsSubscribeOptions = {}) => {
      const includeHidden = opts.includeHidden === true
      const adminTournamentIds = Array.isArray(opts.adminTournamentIds)
        ? [...new Set(opts.adminTournamentIds.filter(Boolean))]
        : []
      const nextKey = JSON.stringify({
        includeHidden,
        adminTournamentIds: [...adminTournamentIds].sort()
      })

      if (activeSubscribeKey === nextKey && listUnsubs.length > 0) return
      stopList()
      activeSubscribeKey = nextKey

      const col = collection(db, 'tournaments')
      if (includeHidden) {
        attachQuerySource(col, 'all')
        return
      }

      // Emulator: listen to the whole collection. Filtered queries + `isVisible == null`
      // are easy to get wrong with seeded/docs missing fields; rules still hide what
      // unauthenticated users cannot read.
      if (import.meta.env.VITE_USE_FIRESTORE_EMULATOR === 'true') {
        attachQuerySource(col, 'emulator-full')
        return
      }

      // Production: visible tournaments (legacy docs with no field use visible-null).
      attachQuerySource(query(col, where('isVisible', '==', true)), 'visible-true')
      attachQuerySource(query(col, where('isVisible', '==', null)), 'visible-null')

      for (const tid of adminTournamentIds) {
        attachDocSource(tid)
      }
    }

    const stopList = () => {
      for (const unsub of listUnsubs) {
        unsub()
      }
      listUnsubs = []
      activeSubscribeKey = null
      sourceMaps = []
      set({ list: [], archivedList: [] })
    }

    return {
      list: [],
      archivedList: [],
      loading: false,
      listSorted: [],

      subscribeAll,
      stopList,

      /**
       * Create a tournament (super-admin callable + Admin SDK — works with emulator auto-seed).
       */
      createTournament: async (payload: any = {}) => {
        try {
          if (!auth.currentUser?.uid) return { success: false, error: 'Not signed in' }
          const fn = httpsCallable(functions, 'adminCreateTournamentDraft')
          const result: any = await fn({
            program: payload.program || undefined
          })
          const d = result.data || {}
          const id = d.tournamentId || d.id
          if (!id) return { success: false, error: 'No tournament id returned.' }
          return { success: true, id }
        } catch (e) {
          console.error('createTournament:', e)
          const msg =
            e?.message || (typeof e === 'string' ? e : 'Could not create tournament.')
          return { success: false, error: msg }
        }
      },

      updateTournament: async (tournamentId, data) => {
        const dref = doc(db, 'tournaments', tournamentId)
        await updateDoc(dref, {
          ...data,
          updatedAt: new Date().toISOString()
        })
        return { success: true }
      },

      /**
       * Super admin "delete": archive hides the tournament everywhere but keeps its
       * registrations, payments and games. Restore clears both fields.
       */
      setTournamentArchived: async (tournamentId, archived) => {
        const uid = auth.currentUser?.uid
        if (!tournamentId) return { success: false, error: 'Missing tournament id' }
        if (!uid) return { success: false, error: 'Not signed in' }
        try {
          await updateDoc(doc(db, 'tournaments', tournamentId), {
            archivedAt: archived ? serverTimestamp() : null,
            archivedBy: archived ? uid : null,
            updatedAt: new Date().toISOString()
          })
          return { success: true }
        } catch (e) {
          console.error('setTournamentArchived:', e)
          return { success: false, error: e?.message || 'Could not update tournament.' }
        }
      },

      /**
       * Create/update a tournament game under tournaments/{id}/games/{gameId}.
       * Team fields are display names (check-in + sheets key off names, not league team ids).
       */
      saveGame: async (tournamentId, gameData) => {
        if (!tournamentId) return { success: false, error: 'Missing tournament id' }
        if (!auth.currentUser?.uid) return { success: false, error: 'Not signed in' }
        try {
          const gameId = gameData.id || randomGameId()
          const ref = doc(db, 'tournaments', tournamentId, 'games', gameId)
          const homeTeamName = String(gameData.homeTeamName || gameData.homeTeam || '').trim()
          const awayTeamName = String(gameData.awayTeamName || gameData.awayTeam || '').trim()
          const payload: any = {
            id: gameId,
            date: normalizeGameDate(gameData.date),
            venue: String(gameData.venue || '').trim() || null,
            homeTeamName: homeTeamName || null,
            awayTeamName: awayTeamName || null,
            // Aliases used by older sheet / label readers
            homeTeam: homeTeamName || null,
            awayTeam: awayTeamName || null,
            homeScore: gameData.homeScore ?? null,
            awayScore: gameData.awayScore ?? null,
            overtimeLoss: gameData.overtimeLoss === true,
            status: gameData.status || 'scheduled',
            notes: gameData.notes || null,
            updatedAt: new Date().toISOString()
          }
          if (!gameData.id) payload.createdAt = new Date().toISOString()
          await setDoc(ref, payload, { merge: true })
          return { success: true, gameId }
        } catch (e) {
          console.error('saveTournamentGame:', e)
          return { success: false, error: e?.message || 'Could not save game.' }
        }
      },

      deleteGame: async (tournamentId, gameId) => {
        if (!tournamentId || !gameId) {
          return { success: false, error: 'Missing tournament or game id' }
        }
        if (!auth.currentUser?.uid) return { success: false, error: 'Not signed in' }
        try {
          await deleteDoc(doc(db, 'tournaments', tournamentId, 'games', gameId))
          return { success: true }
        } catch (e) {
          console.error('deleteTournamentGame:', e)
          return { success: false, error: e?.message || 'Could not delete game.' }
        }
      },

      /**
       * Email approved/paid players about published draft and/or schedule.
       * Also sets the matching publish flags server-side.
       */
      notifyTournamentPublish: async ({
        tournamentId,
        includeDraft = false,
        includeSchedule = false,
        force = false
      }: NotifyTournamentPublishOptions = {}) => {
        if (!tournamentId) return { success: false, error: 'Missing tournament id' }
        if (!auth.currentUser?.uid) return { success: false, error: 'Not signed in' }
        try {
          const fn = httpsCallable(functions, 'notifyTournamentPublish')
          const result: any = await fn({
            tournamentId,
            includeDraft: !!includeDraft,
            includeSchedule: !!includeSchedule,
            force: !!force
          })
          return { success: true, ...(result.data || {}) }
        } catch (e) {
          console.error('notifyTournamentPublish:', e)
          return {
            success: false,
            error: e?.message || 'Could not email players.'
          }
        }
      },

      uploadLogo: async (file, tournamentId) => {
        const uid = auth.currentUser?.uid
        if (!uid) throw new Error('Not signed in')
        const blob = await resizeToWebp(file)
        if (blob.size > LOGO_MAX_BYTES) throw new Error('Image must be under 5 MB')
        const path = logoStoragePath(tournamentId, uid)
        const ref = storageRef(storage, path)
        const body =
          blob instanceof File && blob.type === LOGO_MIME
            ? blob
            : new File([blob], 'logo.webp', { type: LOGO_MIME })
        await uploadBytes(ref, body, { contentType: LOGO_MIME })
        const url = await getDownloadURL(ref)
        return { url, path }
      },

      deleteLogoByPath: async (path) => {
        if (!path) return
        try {
          await deleteObject(storageRef(storage, path))
        } catch (e) {
          if (e?.code !== 'storage/object-not-found') {
            console.warn('Tournament logo delete:', e)
          }
        }
      }
    }
  })
)
